import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import ScoreRing from "@/components/ScoreRing";
import { apiClient } from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import {
  ArrowLeft,
  Sparkles,
  Send,
  RotateCcw,
  Trophy,
  Lightbulb,
  Target,
  Check,
  ArrowRight,
} from "lucide-react";
import { captureNudgeArrival, capture } from "@/lib/analytics";
import ConversionTriggerModal, { useConversionTrigger } from "@/components/ConversionTriggerModal";

export default function LessonPlayer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [progress, setProgress] = useState(null);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fired = useRef(false);
  const trigger = useConversionTrigger();

  useEffect(() => {
    (async () => {
      try {
        const r = await apiClient.get(`/lessons/${id}`);
        setLesson(r.data.lesson);
        setProgress(r.data.progress);
        if (r.data.progress?.last_prompt) setText(r.data.progress.last_prompt);
        captureNudgeArrival("nudge_arrival_lesson", { lesson_id: id });
        capture("lesson_view", { lesson_id: id });
      } catch (e) {
        if (e?.response?.status === 402) {
          navigate("/pricing");
        } else if (e?.response?.status === 401) {
          navigate("/");
        } else {
          setError("Lesson not found");
        }
      }
    })();
  }, [id, navigate]);

  const submit = async () => {
    if (text.trim().length < 5) {
      setError("Write a longer prompt first.");
      return;
    }
    setError(null);
    setSubmitting(true);
    setResult(null);
    try {
      const r = await apiClient.post(`/lessons/${id}/grade`, { prompt_text: text });
      setResult(r.data);
      if (r.data.passed && r.data.completed_now && !fired.current) {
        fired.current = true;
        confetti({
          particleCount: 140,
          spread: 80,
          origin: { y: 0.5 },
          colors: ["#ff5c2a", "#0e9f6e", "#4f46e5", "#0f0f12"],
        });
        // Mark first win — drives the deferred paywall flow.
        try {
          if (!localStorage.getItem("first_challenge_completed_at")) {
            localStorage.setItem(
              "first_challenge_completed_at",
              new Date().toISOString()
            );
            localStorage.setItem("first_win_pending", "1");
            capture("first_challenge_completed", { lesson_id: id });
          }
        } catch (lsErr) {
          // eslint-disable-next-line no-console
          console.warn("[lesson] could not stamp first-win state:", lsErr?.message);
        }

        // Conversion triggers — fire AFTER confetti settles
        try {
          const completedCount = parseInt(localStorage.getItem("lessons_completed_total") || "0", 10) + 1;
          localStorage.setItem("lessons_completed_total", String(completedCount));
          const streak = r.data?.new_streak ?? r.data?.streak;
          const beltUnlocked = !!r.data?.belt_unlocked;

          // 700ms delay for the modal to feel like a reward, not a tax.
          setTimeout(() => {
            if (beltUnlocked) {
              trigger.fireBeltUnlock();
            } else if (streak && streak >= 7) {
              trigger.fireSevenDayStreak();
            } else if (completedCount === 3) {
              trigger.fireThreeChallenges();
            }
          }, 700);
        } catch (trigErr) {
          // eslint-disable-next-line no-console
          console.warn("[lesson] trigger logic failed:", trigErr?.message);
        }
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : detail?.message || "Grading failed. Please try again.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const onContinue = () => {
    let firstWin = false;
    try {
      firstWin = localStorage.getItem("first_win_pending") === "1";
      if (firstWin) localStorage.removeItem("first_win_pending");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[lesson] could not read first_win_pending:", e?.message);
    }
    if (firstWin) {
      navigate("/pricing?first_win=1");
    } else {
      navigate("/dashboard");
    }
  };

  if (error && !lesson) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3">
        <p>{error}</p>
        <Link to="/dashboard" className="btn-ghost">
          Back to dashboard
        </Link>
      </div>
    );
  }
  if (!lesson) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="spinner" />
      </div>
    );
  }

  const status = submitting
    ? "grading"
    : result
      ? result.passed
        ? "passed"
        : "needs-work"
      : "draft";

  const statusCopy = {
    draft: { dot: "var(--ink-3)", text: "writing" },
    grading: { dot: "var(--accent)", text: "checking…" },
    passed: { dot: "var(--emerald)", text: "great work" },
    "needs-work": { dot: "var(--accent)", text: "keep going" },
  }[status];

  return (
    <div className="App min-h-screen">
      <Header />
      <ConversionTriggerModal trigger={trigger.activeTrigger} onDismiss={trigger.dismiss} />      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14 pb-28 md:pb-14">
        <button
          onClick={() => navigate("/dashboard")}
          className="text-sm text-[var(--ink-2)] hover:text-[var(--ink)] inline-flex items-center gap-1.5 mb-7 transition-colors"
          data-testid="lesson-back-button"
        >
          <ArrowLeft size={14} /> Back to dashboard
        </button>

        {/* HEADER: Lesson intro */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="kicker" data-testid="lesson-kicker">Today&rsquo;s Skill · {lesson.concept}</span>
          </div>
          <h1 className="font-display text-5xl md:text-6xl leading-[1.02] tracking-tight max-w-3xl">
            {lesson.title}
          </h1>
        </div>

        {/* SCENARIO BRIEF */}
        <div className="brut p-6 md:p-7 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Target size={14} className="text-[var(--accent)]" />
            <span className="kicker">Today&rsquo;s challenge</span>
          </div>
          <p className="text-[var(--ink)] leading-relaxed text-[1.02rem]">
            {lesson.scenario}
          </p>
        </div>

        {/* PROMPT EDITOR */}
        <div className="rounded-[var(--radius-lg)] overflow-hidden border border-[var(--line)] shadow-[var(--shadow-md)] bg-[var(--ink)]">
          {/* Editor chrome */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
              <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
              <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
              <span className="ml-3 font-mono text-[0.7rem] text-white/45">
                your-prompt.md
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-[0.78rem] text-white/55">
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: statusCopy.dot }}
              />
              {statusCopy.text}
            </div>
          </div>

          {/* Textarea */}
          <textarea
            data-testid="lesson-prompt-textarea"
            className="block w-full bg-transparent text-white/95 placeholder-white/30 outline-none resize-y font-mono text-[0.92rem] leading-[1.7] px-5 py-5 min-h-[240px]"
            placeholder="Write your answer here. Add helpful details so AI knows exactly what to do."
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={submitting}
            spellCheck={false}
          />

          {/* Editor footer */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/5 bg-white/[0.02]">
            <div className="flex items-center gap-3 text-[0.78rem] text-white/45">
              <span data-testid="lesson-char-count">{text.length} chars</span>
              <span className="hidden sm:inline">·</span>
              <span className="hidden sm:inline">~{Math.ceil(text.split(/\s+/).filter(Boolean).length)} words</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setText("");
                  setResult(null);
                }}
                disabled={submitting}
                className="text-[0.78rem] font-medium text-white/55 hover:text-white px-3 py-1.5 rounded-full transition-colors"
                data-testid="lesson-reset-button"
              >
                <RotateCcw size={12} className="inline mr-1" /> Start Over
              </button>
              <button
                onClick={submit}
                disabled={submitting}
                data-testid="lesson-submit-button"
                className="inline-flex items-center gap-1.5 bg-[var(--accent)] hover:bg-[#f15426] text-white text-[0.82rem] font-medium px-4 py-1.5 rounded-full transition-all hover:-translate-y-px shadow-[var(--shadow-glow)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <span className="spinner" /> Checking…
                  </>
                ) : (
                  <>
                    <Send size={12} /> Check My Work
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Tip line */}
        <div className="mt-3 text-xs text-[var(--ink-3)] flex items-center gap-1.5">
          <Sparkles size={11} />
          Tip — say who it&rsquo;s for, what you want, and how to show the answer.
        </div>

        {error && (
          <div
            className="mt-4 rounded-xl border border-[rgba(255,92,42,0.3)] bg-[rgba(255,92,42,0.06)] px-4 py-3 text-sm text-[var(--accent)]"
            data-testid="lesson-error"
          >
            {error}
          </div>
        )}

        {/* SCORE REVEAL */}
        <AnimatePresence>
          {result && (
            <motion.section
              key="result"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ opacity: 0 }}
              className="mt-8"
              data-testid="lesson-score-reveal"
            >
              <div className="brut-lg overflow-hidden">
                {/* Top: title + total */}
                <div className="px-7 md:px-9 pt-7 md:pt-8 pb-5 border-b border-[var(--line)] bg-gradient-to-br from-white to-[var(--bg)]">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <span className="kicker">Skill check</span>
                      <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight mt-2">
                        {result.passed ? (
                          <>
                            Skill <em>unlocked</em>.
                          </>
                        ) : (
                          <>
                            Almost <em>there</em>.
                          </>
                        )}
                      </h2>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`pill ${result.passed ? "pill-emerald" : "pill-accent"}`}
                      >
                        <Trophy size={11} /> {result.scores.total}/30
                      </span>
                      {result.xp_awarded > 0 && (
                        <span className="pill pill-accent">
                          Belt progress earned
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Score rings */}
                <div className="px-5 md:px-9 py-7 grid grid-cols-3 gap-3">
                  <ScoreRing
                    label="Clear"
                    score={result.scores.clarity}
                    color="var(--accent)"
                    delay={0.1}
                  />
                  <ScoreRing
                    label="Detailed"
                    score={result.scores.specificity}
                    color="var(--emerald)"
                    delay={0.25}
                  />
                  <ScoreRing
                    label="Skill used"
                    score={result.scores.technique}
                    color="var(--indigo)"
                    delay={0.4}
                  />
                </div>

                {/* Feedback — structured, plain-language bullets so users
                 *  immediately see what they did well and what to add next.
                 *  The legacy single-paragraph `explanation` is only used as
                 *  a fallback when the new structured fields are missing. */}
                <div className="px-7 md:px-9 pb-7 md:pb-8 space-y-4">
                  {result.completion_message && (
                    <div
                      className="rounded-xl border p-5"
                      style={{
                        borderColor: result.passed ? "rgba(16,185,129,0.35)" : "var(--line)",
                        background: result.passed ? "rgba(16,185,129,0.06)" : "var(--bg)",
                      }}
                      data-testid="lesson-completion-message"
                    >
                      <p className="leading-relaxed text-[var(--ink)] text-base">
                        {result.completion_message}
                      </p>
                    </div>
                  )}

                  {result.what_you_did_well?.length > 0 && (
                    <div className="rounded-xl border border-[var(--line)] bg-[var(--bg)] p-5" data-testid="lesson-did-well">
                      <div className="kicker flex items-center gap-2 mb-2.5">
                        <Check size={12} className="text-[var(--emerald)]" strokeWidth={3} />
                        What you did well
                      </div>
                      <ul className="space-y-2">
                        {result.what_you_did_well.slice(0, 3).map((b, i) => (
                          <li key={`lw-${i}`} className="flex items-start gap-2.5 text-[var(--ink)] leading-relaxed" data-testid={`lesson-did-well-${i}`}>
                            <Check size={14} className="text-[var(--emerald)] flex-shrink-0 mt-1" strokeWidth={2.5} />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {result.how_to_improve?.length > 0 && (
                    <div className="rounded-xl border border-[var(--line)] bg-[var(--bg)] p-5" data-testid="lesson-how-to-improve">
                      <div className="kicker flex items-center gap-2 mb-2.5">
                        <ArrowRight size={12} className="text-[var(--accent)]" strokeWidth={3} />
                        How to improve
                      </div>
                      <ul className="space-y-2">
                        {result.how_to_improve.slice(0, 3).map((b, i) => (
                          <li key={`lh-${i}`} className="flex items-start gap-2.5 text-[var(--ink)] leading-relaxed" data-testid={`lesson-how-to-improve-${i}`}>
                            <ArrowRight size={14} className="text-[var(--accent)] flex-shrink-0 mt-1" strokeWidth={2.5} />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {result.try_this && (
                    <div
                      className="rounded-xl border p-5"
                      style={{ borderColor: "rgba(37,99,235,0.3)", background: "rgba(37,99,235,0.04)" }}
                      data-testid="lesson-try-this"
                    >
                      <div className="kicker flex items-center gap-2 mb-2">
                        <Lightbulb size={12} className="text-[var(--accent)]" />
                        Now try again
                      </div>
                      <p className="leading-relaxed text-[var(--ink)]">{result.try_this}</p>
                      {/* "Need help understanding this?" — paid users jump
                       *  straight to their AI Coach with this lesson's
                       *  scenario as context; free users see the same link
                       *  but the coach page renders the paywall. */}
                      <div className="mt-3 pt-3 border-t border-[rgba(37,99,235,0.18)] flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-[var(--ink-3)]">
                          Need help understanding this?
                        </span>
                        <Link
                          to="/coach"
                          onClick={() => capture("ai_coach_clicked", { source: "lesson_hook", lesson_id: id })}
                          className="text-xs font-semibold inline-flex items-center gap-1 text-[var(--primary,#2563EB)] hover:underline"
                          data-testid="lesson-ask-coach"
                        >
                          Ask Your AI Coach <ArrowRight size={11} />
                        </Link>
                      </div>
                    </div>
                  )}

                  {result.hint_examples?.length > 0 && (
                    <div className="rounded-xl border border-[var(--line)] bg-[var(--bg)] p-5" data-testid="lesson-hints">
                      <div className="kicker flex items-center gap-2 mb-3">
                        <Sparkles size={12} className="text-[var(--accent)]" />
                        Hint examples
                      </div>
                      <p className="text-sm text-[var(--ink-2)] leading-relaxed mb-3">
                        Try adding details like these to your own prompt. These
                        are small snippets — not a finished answer.
                      </p>
                      <div className="grid sm:grid-cols-2 gap-2.5">
                        {result.hint_examples.slice(0, 3).map((h, i) => (
                          <div
                            key={`lh-hint-${i}`}
                            className="rounded-lg border p-3 flex items-start gap-2"
                            style={{ borderColor: "rgba(37,99,235,0.22)", background: "rgba(37,99,235,0.03)" }}
                            data-testid={`lesson-hint-${i}`}
                          >
                            <span className="text-[var(--accent)] font-bold text-sm flex-shrink-0">“</span>
                            <span className="text-sm text-[var(--ink)] leading-relaxed font-medium">{h}</span>
                            <span className="text-[var(--accent)] font-bold text-sm flex-shrink-0">”</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Fallback for older callers that only have `explanation`. */}
                  {!result.what_you_did_well?.length && !result.how_to_improve?.length && result.explanation && (
                    <div className="rounded-xl border border-[var(--line)] bg-[var(--bg)] p-5" data-testid="lesson-feedback-legacy">
                      <div className="kicker flex items-center gap-2">
                        <Lightbulb size={12} className="text-[var(--accent)]" />
                        Feedback
                      </div>
                      <p className="mt-2.5 leading-relaxed text-[var(--ink)]">
                        {result.explanation}
                      </p>
                    </div>
                  )}
                  {/* The "Model answer" / "Example prompt" copy-paste block
                   *  was removed on purpose: PromptDojo never hands users a
                   *  finished prompt — they must rewrite in their own words. */}
                </div>

                {/* CTA row — friendly, progress-oriented language */}
                <div className="px-7 md:px-9 pb-7 md:pb-8 flex flex-wrap gap-3 border-t border-[var(--line)] pt-6">
                  <button
                    onClick={onContinue}
                    className="btn-primary"
                    data-testid="lesson-continue-button"
                  >
                    {result.passed ? "Unlock Next Skill" : "Keep Learning"} →
                  </button>
                  <button
                    onClick={() => {
                      setResult(null);
                      fired.current = false;
                    }}
                    className="btn-ghost"
                    data-testid="lesson-try-again-button"
                  >
                    Practice Again
                  </button>
                </div>
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
